import { basketAtom, basketLoaderAtom, storeDataAtom } from "@/lib/atoms";
import { useApi } from "@/lib/pariette";
import { Product } from "@/lib/types";
import { useAtom, useAtomValue } from "jotai";
import { useCallback, useState } from "react";
import { toast } from "sonner";

const useBasket = () => {
  const [basket, setBasket] = useAtom(basketAtom);
  const [loader, setLoader] = useAtom(basketLoaderAtom);
  const store = useAtomValue(storeDataAtom);
  const { $get, $post, $put } = useApi();
  const [recommendedProducts, setRecommendedProducts] = useState<Product[]>();
  const [recommendedProductsLoader, setRecommendedProductsLoader] = useState<boolean>(false);

  const setBasketCode = useCallback((basketCode: string, storeToken: string) => {
    localStorage.setItem("basketCodes", JSON.stringify({ ...JSON.parse(localStorage.getItem("basketCodes") ?? "{}"), [storeToken]: basketCode }));
  }, []);

  const removeBasketCode = useCallback(() => {
    if (!store) {
      return;
    }
    localStorage.setItem("basketCodes", JSON.stringify({ ...JSON.parse(localStorage.getItem("basketCodes") ?? "{}"), [store.token]: null }));
    setBasket(null);
  }, [setBasket, store]);

  const findBasketCode = useCallback((storeToken: string | undefined) => {
    if (!storeToken) {
      return;
    }
    const basketCodes = JSON.parse(localStorage.getItem("basketCodes") ?? "{}");
    return basketCodes[storeToken];
  }, []);

  const getBasket = useCallback(
    async (newCode?: string, newStore?: string) => {
      const storeToken = newStore ?? store?.token;
      const basket = newCode ?? findBasketCode(storeToken);
      if (!basket || !storeToken) {
        setBasket(null);
        setLoader(false);
        return;
      }

      setLoader(true);
      await $get(`basket/${basket}?group=1`)
        .then((res) => {
          setBasketCode(basket, storeToken);
          setBasket(res.data);
        })
        .catch(() => setBasket(null))
        .finally(() => setLoader(false));
    },
    [$get, setBasket, setBasketCode, setLoader, findBasketCode, store],
  );

  const productAmount = useCallback((token: string): number => basket?.items?.find((p) => p.product === token)?.amount ?? 0, [basket]);

  const editOrAddProductToBasket = useCallback(
    async (product: { token: string; currency: any; type: string }, amount: number) => {
      const basketType = basket && basket.items?.length > 0 ? (basket.items.every((item) => item.type === "phone") ? "phone" : "other") : undefined;
      if (basketType && (basketType === "phone") !== (product.type === "phone")) {
        console.log(basketType, product.type);
        toast.error("Der Artikel konnte nicht in Ihren Warenkorb gelegt werden. Ihr Warenkorb enthält einen anderen Artikeltyp. Telefone können nur einzeln erworben werden.", {
          richColors: true,
          position: "top-center",
          className: "!text-xl",
        });
        return;
      }
      const basketAmount = productAmount(product.token);
      const form =
        basketAmount > 0
          ? {
              basketCode: basket?.basket_code,
              amount,
              basket_status: null,
            }
          : {
              basketCode: basket?.basket_code,
              amount: amount ?? 1,
              product: product.token,
              currency: product.currency,
              type: product.type,
              payment_type: "credit",
              note: null,
              handynet: true,
            };
      const api = basketAmount > 0 ? $put(`basket-item/${product.token}`, form) : $post("basket", form);

      return api.then((res) => {
        toast.success("Warenkorb aktualisiert.", {
          richColors: true,
          position: "top-center",
          className: "!text-xl",
        });
        getBasket(basketAmount > 0 ? undefined : res.data.basket_code);
        return res;
      });
    },
    [$post, $put, basket, getBasket, productAmount],
  );

  const getRecommendedProducts = useCallback(async (): Promise<Product[] | undefined> => {
    if (!basket?.basket_code) return;
    setRecommendedProductsLoader(true);
    await $get(`recommended-basket/${basket.basket_code}`)
      .then((res) => {
        setRecommendedProducts(res.data);
      })
      .finally(() => setRecommendedProductsLoader(false));
  }, [basket, $get]);

  return { basket, getBasket, productAmount, loader, removeBasketCode, editOrAddProductToBasket, getRecommendedProducts, recommendedProducts, recommendedProductsLoader };
};

export default useBasket;
